import { useState } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { colors, shadows } from "../source/source.styles";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Label,
} from "recharts";
import CircleIcon from "@mui/icons-material/Circle";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const DSPSourceTrendChart = ({ data = { labels: [], leads: [], conversions: [] }, dateRange }) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const [gradientId] = useState(`sourceTrendGradient-${Math.random().toString(36).substr(2, 9)}`);

  // Check if single date selected
  const isSingleDate =
    dateRange?.startDate &&
    dateRange?.endDate &&
    new Date(dateRange.startDate).toDateString() === new Date(dateRange.endDate).toDateString();

  // Transform data for chart - calculate actual conversion count
  const chartData = data.labels.map((label, index) => {
    const leads = data.leads[index] || 0;
    const rate = data.conversions[index] || 0;
    const conversions = Math.round((leads * rate) / 100);
    return {
      name: label,
      leads: leads,
      conversionRate: rate,
      conversions: conversions,
    };
  });

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const leadsData = payload.find((p) => p.dataKey === "leads");
    const convRateData = payload.find((p) => p.dataKey === "conversionRate");

    // Get conversion count from payload
    const conversions = convRateData?.payload?.conversions || 0;

    return (
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          px: 2,
          py: 1.5,
          borderRadius: 2,
          boxShadow: "0 8px 24px rgba(0,0,0,.15)",
          border: `1px solid ${colors.borderLight}`,
          minWidth: 160,
        }}
      >
        <Typography fontSize={11} color={colors.textMuted} mb={1}>
          {label}
        </Typography>
        <Typography fontSize={13} fontWeight={600} color={colors.textPrimary} mb={1.5}>
          Performance
        </Typography>

        {/* Leads */}
        {leadsData && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box sx={{ width: 8, height: 8, borderRadius: 1, bgcolor: primaryColor }} />
              <Typography fontSize={11} color={colors.textMuted}>Leads</Typography>
            </Stack>
            <Typography fontSize={14} fontWeight={700} color={colors.textPrimary}>
              {leadsData.value}
            </Typography>
          </Stack>
        )}

        {/* Conversions Count */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Box sx={{ width: 8, height: 8, borderRadius: 1, bgcolor: colors.success }} />
            <Typography fontSize={11} color={colors.textMuted}>Conversions</Typography>
          </Stack>
          <Typography fontSize={14} fontWeight={700} color={colors.success}>
            {conversions}
          </Typography>
        </Stack>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={2}>
        <Box>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              color: colors.textPrimary,
              mb: 0.5,
            }}
          >
            Lead Generation Trends
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: primaryColor,
            }}
          >
            {isSingleDate ? "Hourly breakdown for selected date" : "Daily breakdown for selected period"}
          </Typography>
        </Box>

        {/* Legend */}
        <Stack direction="row" spacing={3}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircleIcon sx={{ fontSize: 8, color: primaryColor }} />
            <Typography fontSize={11} fontWeight={500} color={colors.textSecondary}>
              LEADS
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 16,
                height: 2,
                bgcolor: colors.success,
                borderRadius: 1,
              }}
            />
            <Typography fontSize={11} fontWeight={500} color={colors.textSecondary}>
              CONVERSIONS & RATE
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      {/* Chart */}
      <Box sx={{ height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={primaryColor} stopOpacity={0.9} />
                <stop offset="100%" stopColor={primaryColor} stopOpacity={0.6} />
              </linearGradient>
            </defs>

            <CartesianGrid vertical={false} stroke={colors.borderLight} strokeDasharray="4 4" />

            <XAxis
              dataKey="name"
              tick={{ fill: colors.textMuted, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              dy={10}
              tickFormatter={(value) => {
                // For hourly data, show every 3rd hour to avoid crowding
                if (isSingleDate && value.includes(':')) {
                  const hour = parseInt(value.split(':')[0]);
                  return hour % 3 === 0 ? value : '';
                }
                return value;
              }}
              interval={isSingleDate ? 0 : "preserveStartEnd"}
            />

            <YAxis
              yAxisId="left"
              tick={{ fill: colors.textMuted, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              dx={-10}
              width={45}
            />

            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fill: colors.textMuted, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => `${value}%`}
              dx={10}
              width={40}
              domain={[0, 100]}
            />

            <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />

            <Bar
              yAxisId="left"
              dataKey="leads"
              fill={`url(#${gradientId})`}
              radius={[4, 4, 0, 0]}
              barSize={40}
            />

            <Line
              yAxisId="right"
              type="monotone"
              dataKey="conversionRate"
              stroke={colors.success}
              strokeWidth={2.5}
              dot={{
                r: 4,
                fill: "#fff",
                stroke: colors.success,
                strokeWidth: 2,
              }}
              activeDot={{
                r: 6,
                fill: colors.success,
                stroke: "#fff",
                strokeWidth: 2,
              }}
              label={(props) => {
                const { x, y, value, payload } = props;
                const leads = payload?.leads || 0;
                const conv = payload?.conversions || 0;

                // For hourly data, only show labels for hours with significant activity
                const hour = parseInt(payload?.name?.split(':')[0]) || 0;
                const shouldShow = isSingleDate
                  ? (leads >= 2 || hour % 4 === 0) // Show every 4th hour or when there are 2+ leads
                  : (leads >= 5); // For daily data, show when 5+ leads

                if (shouldShow && value >= 0) {
                  return (
                    <g>
                      <rect
                        x={x - 22}
                        y={y - 38}
                        width={44}
                        height={30}
                        rx={5}
                        fill={conv > 0 ? colors.success : "#9CA3AF"}
                        opacity={0.95}
                      />
                      <text
                        x={x}
                        y={y - 24}
                        textAnchor="middle"
                        fontSize={12}
                        fontWeight={700}
                        fill="#FFF"
                      >
                        {conv}
                      </text>
                      <text
                        x={x}
                        y={y - 13}
                        textAnchor="middle"
                        fontSize={10}
                        fontWeight={600}
                        fill="#FFF"
                        opacity={0.95}
                      >
                        {Math.round(value * 10) / 10}%
                      </text>
                    </g>
                  );
                }
                return null;
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default DSPSourceTrendChart;
